import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { X, PlusCircle, Pencil, Image, Video, FileText, File, Loader2, Eye } from 'lucide-react';
import { extractApiErrorMessage, getAcceptedLocationStablesByFarmType, notifications } from '../../../../utils';
import { LocationStableTranslations } from '../../../../models/locations';
import { EnumCheckList } from '../../../../elements';
import { RichTextEditor } from '../../../../elements/RichTextEditor';
import useAuthContext from '../../../../hooks/useAuthContext';
import { useAttachmentUpload } from '../../../../hooks/useAttachmentUpload';
import { AzureBlobService } from '../../../../hooks/AzureBlobService';
import UserRole from '../../../../constants/user-role';
import { MAX_NAME_LENGTH } from '../../utils/constants';
import { ApiService } from '../../../../hooks';
import {
  HealthSystemDomainTaskLibraryCommandUpsertTaskTemplateCommand,
  HealthSystemDomainTaskLibraryResultTaskTemplateViewModel
} from '../../../../api/HealthSystemApi';

/**
 * The fields this modal reads and saves. Editing passes a whole task template, which satisfies it;
 * creating passes `{}`, and the missing `id` is what switches the modal to create mode.
 */
export type TaskDraft = Partial<
  Pick<
    HealthSystemDomainTaskLibraryResultTaskTemplateViewModel,
    'id' | 'name' | 'instructions' | 'duration' | 'durationUnit' | 'scheduled' | 'stables' | 'attachments'
  >
>;

type TaskAttachment = NonNullable<TaskDraft['attachments']>[number];

interface CreateUpdateTaskModalProps {
  opened: boolean;
  task: TaskDraft;
  onModalAction: (result: boolean) => void;
}

const TASK_MIN_NAME_LENGTH = 3;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentIcon({ contentType }: { contentType: string }) {
  if (contentType.startsWith('image/')) return <Image size={14} className="text-blue-500 flex-shrink-0" />;
  if (contentType.startsWith('video/')) return <Video size={14} className="text-purple-500 flex-shrink-0" />;
  if (contentType === 'application/pdf' || contentType.includes('document') || contentType.includes('word')) {
    return <FileText size={14} className="text-orange-500 flex-shrink-0" />;
  }
  return <File size={14} className="text-gray-500 flex-shrink-0" />;
}

const CreateUpdateTaskModal: React.FC<CreateUpdateTaskModalProps> = (props) => {
  const { opened, task: initialTask, onModalAction } = props;

  const { t } = useTranslation();
  const { state } = useAuthContext();
  const { uploadFile, isUploading } = useAttachmentUpload();
  const azureBlobService = React.useRef(new AzureBlobService()).current;

  const apiService = new ApiService();

  const [task, setTask] = React.useState<TaskDraft>(initialTask);
  const [isBusy, setIsBusy] = React.useState(false);
  const [nameError, setNameError] = React.useState(false);
  const [stablesError, setStablesError] = React.useState(false);
  const [durationError, setDurationError] = React.useState(false);
  const [durationUnitError, setDurationUnitError] = React.useState(false);

  const editMode = !!task.id;
  const dialogTitle = editMode ? t('f-tasks-manager-edit-task-title') : t('f-tasks-manager-add-task-title');
  const saveButtonTitle = editMode ? t('f-com-edit') : t('f-com-create');

  const canEditInstructions = state.role === UserRole.admin || state.role === UserRole.manager;

  const minutesLabel = t('f-com-minutes');
  const hoursLabel = t('f-com-hours');

  React.useEffect(() => {
    if (opened) {
      setTask({ ...initialTask });
      if (!editMode) {
        clearFields();
      }
    }
  }, [opened]);

  const clearFields = () => {
    setTask((prev) => ({
      ...prev,
      name: '',
      instructions: '',
      duration: '',
      durationUnit: '',
      scheduled: '',
      stables: [],
      attachments: []
    }));
    setNameError(false);
    setStablesError(false);
    setDurationError(false);
    setDurationUnitError(false);
  };

  const handleClose = () => onModalAction(false);

  const handleSave = () => {
    const command = buildCreateCommand();

    if (validateForm(command)) {
      setIsBusy(true);

      apiService.api.taskList
        .taskListUpsertTaskLibrary(command)
        .then(() => {
          onModalAction(true);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  };

  const buildCreateCommand = (): HealthSystemDomainTaskLibraryCommandUpsertTaskTemplateCommand => {
    return {
      id: task.id,
      name: task.name ?? '',
      instructions: task.instructions,
      duration: task.duration,
      durationUnit: task.durationUnit,
      scheduled: task.scheduled,
      stables: task.stables ?? [],
      attachments: (task.attachments ?? []).map((a) => ({
        fileName: a.fileName,
        blobUrl: a.blobUrl,
        contentType: a.contentType,
        fileSizeBytes: a.fileSizeBytes
      }))
    };
  };

  const validateForm = (input: HealthSystemDomainTaskLibraryCommandUpsertTaskTemplateCommand): boolean => {
    let valid = true;

    if (!input.name || input.name.length < TASK_MIN_NAME_LENGTH) {
      setNameError(true);
      valid = false;
    } else {
      setNameError(false);
    }

    if (!input.stables || input.stables.length === 0) {
      setStablesError(true);
      valid = false;
    } else {
      setStablesError(false);
    }

    if (!input.duration || Number(input.duration) <= 0) {
      setDurationError(true);
      valid = false;
    } else {
      setDurationError(false);
    }

    if (!input.durationUnit) {
      setDurationUnitError(true);
      valid = false;
    } else {
      setDurationUnitError(false);
    }

    return valid;
  };

  const handleUnitSelect = (unit: string) => {
    setTask({ ...task, durationUnit: unit });
    setDurationUnitError(false);
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    const result = await uploadFile(file);
    if (result) {
      setTask((prev) => ({
        ...prev,
        // Optimistic entry: the backend assigns `id`/`createdAt` on save. Neither field is read
        // before the template reloads, so the cast is narrower than `any` but still needed here.
        attachments: [...(prev.attachments ?? []), result as TaskAttachment]
      }));
      return result.blobUrl;
    }
    return '';
  };

  const handleFileUpload = async (file: File): Promise<void> => {
    const result = await uploadFile(file);
    if (result) {
      setTask((prev) => ({
        ...prev,
        // Optimistic entry: the backend assigns `id`/`createdAt` on save. Neither field is read
        // before the template reloads, so the cast is narrower than `any` but still needed here.
        attachments: [...(prev.attachments ?? []), result as TaskAttachment]
      }));
    }
  };

  const handlePreviewAttachment = async (blobUrl: string) => {
    try {
      const url = await azureBlobService.getDownloadUrl(blobUrl);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      notifications.error('Failed to open file');
    }
  };

  const removeAttachment = (index: number) => {
    setTask((prev) => ({
      ...prev,
      attachments: (prev.attachments ?? []).filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog
      id="addTaskModal"
      open={opened}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="addTaskTitle" className="!bg-gray-50">
        {dialogTitle}
      </DialogTitle>

      <DialogContent dividers>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5 pt-1">
          {/* Name */}
          <div>
            <TextField
              label={t('f-tasks-manager-add-name')}
              size="small"
              fullWidth
              variant="outlined"
              value={task.name ?? ''}
              onChange={(e) => setTask({ ...task, name: e.target.value })}
              error={nameError}
              helperText={nameError ? t('f-tasks-manager-name-required') : ''}
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
            />
          </div>

          {/* Duration + Time unit */}
          <div className="flex gap-2 items-start">
            <div className="w-28 flex-shrink-0">
              <TextField
                label={t('f-tasks-manager-duration')}
                variant="outlined"
                fullWidth
                value={task.duration ?? ''}
                size="small"
                type="number"
                error={durationError}
                helperText={durationError ? t('f-tasks-manager-duration-required') : ''}
                onChange={(e) => {
                  setTask({ ...task, duration: e.target.value });
                  setDurationError(false);
                }}
                inputProps={{ min: 1 }}
              />
            </div>

            <div className="flex flex-col flex-1">
              <div
                className={`flex h-10 rounded border overflow-hidden ${durationUnitError ? 'border-red-500' : 'border-gray-400'}`}
              >
                <button
                  type="button"
                  onClick={() => handleUnitSelect(minutesLabel)}
                  className={`flex-1 text-sm font-medium transition-colors ${
                    task.durationUnit === minutesLabel
                      ? '!bg-blue-600 !text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {minutesLabel}
                </button>
                <button
                  type="button"
                  onClick={() => handleUnitSelect(hoursLabel)}
                  className={`flex-1 text-sm font-medium border-l border-gray-400 transition-colors ${
                    task.durationUnit === hoursLabel
                      ? '!bg-blue-600 !text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {hoursLabel}
                </button>
              </div>
              {durationUnitError && (
                <span className="text-xs text-red-500 mt-1">{t('f-tasks-manager-duration-unit-required')}</span>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('f-tasks-manager-instructions')}
              </p>
              {isUploading && (
                <span className="flex items-center gap-1 text-xs text-blue-500">
                  <Loader2 size={12} className="animate-spin" />
                  Uploading…
                </span>
              )}
            </div>

            {canEditInstructions ? (
              <RichTextEditor
                value={task.instructions ?? ''}
                onChange={(html) => setTask((prev) => ({ ...prev, instructions: html }))}
                onImageUpload={handleImageUpload}
                onFileUpload={handleFileUpload}
              />
            ) : (
              <div
                className="prose prose-sm max-w-none p-3 border border-gray-200 rounded min-h-[80px] text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: task.instructions ?? '' }}
              />
            )}

            {/* Attachment list (images uploaded via editor toolbar appear here) */}
            {(task.attachments ?? []).length > 0 && (
              <div className="mt-2 space-y-1">
                {(task.attachments ?? []).map((att, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-1.5"
                  >
                    <AttachmentIcon contentType={att.contentType} />
                    <span className="flex-1 truncate text-xs text-gray-700">{att.fileName}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(att.fileSizeBytes)}</span>
                    <button
                      type="button"
                      onClick={() => handlePreviewAttachment(att.blobUrl)}
                      className="text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
                      title="Preview"
                    >
                      <Eye size={14} />
                    </button>
                    {canEditInstructions && (
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stables */}
          <div className="sm:col-span-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t('f-tasks-manager-stables')}
            </p>
            <div className="[&_.MuiFormControl-root]:!grid [&_.MuiFormControl-root]:!grid-cols-2 sm:[&_.MuiFormControl-root]:!grid-cols-3 [&_.MuiFormControl-root]:!w-full">
              <EnumCheckList
                onChange={(stables) => setTask({ ...task, stables })}
                selectedValues={editMode ? task.stables : []}
                acceptedValues={getAcceptedLocationStablesByFarmType(state.farmType)}
                translations={LocationStableTranslations}
              />
            </div>
            {stablesError && <p className="text-red-500 text-sm mt-1">{t('f-employees-location-required')}</p>}
          </div>
        </div>
      </DialogContent>

      <DialogActions className="!bg-gray-50">
        {isBusy && <CircularProgress size={20} />}

        <Button onClick={handleClose} variant="outlined" startIcon={<X size={16} />} color="primary">
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={handleSave}
          id="addRuleBtn"
          disabled={isBusy}
          startIcon={editMode ? <Pencil size={16} /> : <PlusCircle size={16} />}
          variant="contained"
          color="primary"
        >
          {saveButtonTitle}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateTaskModal;
